import type { BlogCategory, BlogLevel } from './blog-types';

export interface CategoryDescriptor {
  readonly slug: BlogCategory;
  readonly label: string;
  readonly description: string;
}

export const CATEGORIES: readonly CategoryDescriptor[] = [
  {
    slug: 'technique',
    label: 'Technique',
    description:
      'Left hand, right hand, slap, and tapping — the physical craft of playing bass guitar.',
  },
  {
    slug: 'theory-and-harmony',
    label: 'Theory & Harmony',
    description:
      'Scales, modes, triads, arpeggios, and chord tones applied to bass.',
  },
  {
    slug: 'repertoire-and-reading',
    label: 'Repertoire & Reading',
    description:
      'Reading notation, walking bass lines, solo construction, and song vocabulary.',
  },
  {
    slug: 'practice-and-mindset',
    label: 'Practice & Mindset',
    description:
      'How to practice, exercises, ear training, and the mental side of learning bass.',
  },
  {
    slug: 'gear-and-setup',
    label: 'Gear & Setup',
    description:
      'Bass guitar gear, Zoom setup, and tools for effective online lessons.',
  },
  {
    slug: 'choosing-lessons',
    label: 'Choosing Lessons',
    description:
      'Comparing lesson formats, costs, and approaches so you can pick what fits.',
  },
] as const;

export const CATEGORY_LABELS: Readonly<Record<string, string>> =
  Object.fromEntries(CATEGORIES.map((c) => [c.slug, c.label]));

export const CATEGORY_SLUG_SET: ReadonlySet<BlogCategory> = new Set(
  CATEGORIES.map((c) => c.slug),
);

export interface LevelDescriptor {
  readonly slug: BlogLevel;
  readonly label: string;
  readonly description: string;
}

export const LEVELS: readonly LevelDescriptor[] = [
  {
    slug: 'beginner',
    label: 'Beginner',
    description:
      'First steps on bass, core fundamentals, and how to start out the right way.',
  },
  {
    slug: 'intermediate',
    label: 'Intermediate',
    description:
      'Technique, theory, and reading pulled together to play real songs with confidence.',
  },
  {
    slug: 'advanced',
    label: 'Advanced',
    description:
      'Advanced technique, deeper harmony, and the finer points of playing at a high level.',
  },
] as const;

export const LEVEL_LABELS: Readonly<Record<string, string>> =
  Object.fromEntries(LEVELS.map((l) => [l.slug, l.label]));

export const LEVEL_SLUG_SET: ReadonlySet<BlogLevel> = new Set(
  LEVELS.map((l) => l.slug),
);

export function isBlogCategory(value: string): value is BlogCategory {
  return CATEGORY_SLUG_SET.has(value as BlogCategory);
}

export function isBlogLevel(value: string): value is BlogLevel {
  return LEVEL_SLUG_SET.has(value as BlogLevel);
}
